// Derives the date range Quick View asks the period-analytics endpoint for.
// The backend owns period bucketing and labelling, returning monthly through
// yearly columns for that one range in a single response - so switching the
// period filter re-renders from data already in memory instead of re-querying.

/**
 * Returns the last calendar day of a given month/year.
 *
 * @param {number} year - Full calendar year.
 * @param {number} month - Month, 1-12.
 * @returns {number} The last day number.
 */
const lastDayOfMonth = (year, month) => new Date(year, month, 0).getDate();

/**
 * Formats a date as YYYY-MM-DD from explicit parts, so no timezone conversion
 * can shift the day (Date#toISOString would, for anyone away from UTC).
 *
 * @param {number} year - Full calendar year.
 * @param {number} month - Month, 1-12.
 * @param {number} day - Day of month.
 * @returns {string} e.g. "2026-04-01".
 */
const isoDate = (year, month, day) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

/**
 * Converts a financial year label into the inclusive date range it covers,
 * honouring the company's fiscal starting month. A January start keeps the year
 * inside one calendar year; any later start month ends in the next one.
 *
 *   ("2026-2027", 4) → 2026-04-01 … 2027-03-31
 *   ("2026-2027", 7) → 2026-07-01 … 2027-06-30
 *   ("2026-2026", 1) → 2026-01-01 … 2026-12-31
 *
 * @param {string} financialYearValue - e.g. "2026-2027".
 * @param {number} startingMonth - Fiscal year start month (1-12).
 * @returns {{fromDate: string, toDate: string}|null} Null if unparseable.
 */
export const buildFinancialYearRange = (financialYearValue, startingMonth) => {
  const startYear = parseInt(String(financialYearValue).split("-")[0], 10);
  if (Number.isNaN(startYear) || !startingMonth) return null;

  const start = Number(startingMonth);
  const endYear = start === 1 ? startYear : startYear + 1;
  const endMonth = ((start - 2 + 12) % 12) + 1;

  return {
    fromDate: isoDate(startYear, start, 1),
    toDate: isoDate(endYear, endMonth, lastDayOfMonth(endYear, endMonth)),
  };
};
